import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { FlaskConical, Trophy, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const LABELS = {
  popup: {
    heading: 'Popup A/B-Test (Rapperswil)',
    signupHint: 'Beim Signup: variant + visitor_id im bestehenden\n  //    POST /api/website-analytics/rapperswil/signup Payload mitsenden',
  },
  newsletter: {
    heading: 'Newsletter A/B-Test',
    signupHint: 'Beim Abonnieren: variant + visitor_id im bestehenden\n  //    POST /api/website-analytics/newsletter/subscribe Payload mitsenden',
  },
};

const PopupAbTab = ({ testKey = 'popup' }) => {
  const PATH = `${API}/website-analytics/${testKey}`;
  const tid = `${testKey}-ab`;
  const labels = LABELS[testKey];
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAb = async () => {
    try {
      const res = await axios.get(`${PATH}/ab`, authHeaders());
      setConfig(res.data.config);
      setStats(res.data.stats);
    } catch (err) {
      toast.error('Fehler beim Laden des A/B-Tests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAb(); }, [testKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await axios.put(`${PATH}/ab`, {
        enabled: config.enabled,
        min_impressions: Number(config.min_impressions) || 200,
        variants: config.variants,
        ...extra,
      }, authHeaders());
      toast.success('A/B-Test gespeichert');
      fetchAb();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const resetTest = async () => {
    if (!window.confirm('Test wirklich zurücksetzen?\n\nAlle Impressionen/Signups werden gelöscht und der Gewinner entfernt.')) return;
    try {
      const r = await axios.post(`${PATH}/ab/reset`, {}, authHeaders());
      toast.success(`Test zurückgesetzt (${r.data.events_deleted} Events gelöscht)`);
      fetchAb();
    } catch (err) {
      toast.error('Zurücksetzen fehlgeschlagen');
    }
  };

  const setVariantField = (key, field, value) => {
    setConfig((c) => ({ ...c, variants: { ...c.variants, [key]: { ...c.variants[key], [field]: value } } }));
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-64" data-testid={`${tid}-loading`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    );
  }

  const winner = config.winner;

  return (
    <div className="space-y-6" data-testid={`${tid}-tab`}>
      {/* Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="w-5 h-5 text-amber-600" />
            {labels.heading}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {winner ? (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid={`${tid}-winner-banner`}>
              <Trophy className="w-6 h-6 text-emerald-600 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-800">Variante {winner} hat gewonnen! 🏆</p>
                <p className="text-xs text-emerald-700">Die Website liefert jetzt nur noch Variante {winner} aus.</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => save({ winner: '' })} data-testid={`${tid}-resume-btn`}>
                Test fortsetzen
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid={`${tid}-running-banner`}>
              <FlaskConical className="w-5 h-5 text-blue-600 shrink-0" />
              <p className="text-sm text-blue-800">
                Test läuft — sobald beide Varianten <strong>{config.min_impressions}</strong> Impressionen erreicht haben
                und der Unterschied statistisch signifikant ist, gewinnt automatisch die bessere Variante.
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <Switch checked={!!config.enabled} onCheckedChange={(v) => setConfig((c) => ({ ...c, enabled: v }))} data-testid={`${tid}-enabled-switch`} />
              {testKey === 'popup' ? 'Popup aktiv' : 'Widget-Test aktiv'}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Min. Impressionen pro Variante:
              <Input
                type="number"
                className="w-24 h-8"
                value={config.min_impressions}
                onChange={(e) => setConfig((c) => ({ ...c, min_impressions: e.target.value }))}
                data-testid={`${tid}-min-impressions-input`}
              />
            </label>
            <div className="flex gap-2 ml-auto">
              <Button size="sm" onClick={() => save()} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid={`${tid}-save-btn`}>
                <Save className="w-3.5 h-3.5 mr-1" /> Speichern
              </Button>
              <Button size="sm" variant="outline" onClick={resetTest} className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`${tid}-reset-btn`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Test zurücksetzen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Varianten */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {['A', 'B'].map((key) => {
          const v = config.variants?.[key] || {};
          const s = stats?.[key] || { impressions: 0, signups: 0, conversion_rate: 0 };
          const isWinner = winner === key;
          return (
            <Card key={key} className={isWinner ? 'border-emerald-400 ring-1 ring-emerald-300' : ''} data-testid={`${tid}-variant-${key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${key === 'A' ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'}`}>{key}</span>
                  Variante {key}
                  {isWinner && <Trophy className="w-4 h-4 text-emerald-500" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Titel</p>
                  <Input value={v.title || ''} onChange={(e) => setVariantField(key, 'title', e.target.value)} data-testid={`${tid}-${key}-title-input`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Text</p>
                  <Input value={v.text || ''} onChange={(e) => setVariantField(key, 'text', e.target.value)} data-testid={`${tid}-${key}-text-input`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Button-Text</p>
                  <Input value={v.button_text || ''} onChange={(e) => setVariantField(key, 'button_text', e.target.value)} data-testid={`${tid}-${key}-button-input`} />
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                  <StatBox label="Impressionen" value={s.impressions} testId={`${tid}-${key}-impressions`} />
                  <StatBox label="Anmeldungen" value={s.signups} testId={`${tid}-${key}-signups`} />
                  <StatBox label="Conv.-Rate" value={`${s.conversion_rate}%`} highlight testId={`${tid}-${key}-rate`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Integration für Website-Agent */}
      <Card className="border-dashed border-gray-300 bg-gray-50/50" data-testid={`${tid}-integration-hint`}>
        <CardContent className="py-5">
          <p className="text-sm text-gray-600 font-medium mb-2">Integration auf www.strongfit.ch (an Website-Agent weitergeben)</p>
          <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto">
{`var API = "${process.env.REACT_APP_BACKEND_URL}/api/website-analytics/${testKey}";
var vid = localStorage.getItem('sf_vid') || crypto.randomUUID();
localStorage.setItem('sf_vid', vid);

// 1. Variante holen und Widget damit rendern
fetch(API + '/variant?visitor_id=' + vid).then(r => r.json()).then(function(d) {
  if (!d.enabled) return;
  // d.variant = "A"|"B", d.config = { title, text, button_text }
  render(d.config);
  // 2. Impression melden (beim Anzeigen)
  fetch(API + '/event', { method: 'POST', headers: {'Content-Type':'application/json'},
    body: JSON.stringify({ variant: d.variant, event: 'impression', visitor_id: vid }) });
  // 3. ${labels.signupHint}
});`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

const StatBox = ({ label, value, highlight, testId }) => (
  <div className={`rounded-lg px-2 py-2 text-center ${highlight ? 'bg-amber-50' : 'bg-gray-50'}`} data-testid={testId}>
    <p className={`text-lg font-bold ${highlight ? 'text-amber-700' : 'text-gray-900'}`}>{value}</p>
    <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
  </div>
);

export default PopupAbTab;
